import { CameraTargetPoint } from "./CameraTargetPoint";
import { Signal } from "signals.js";

import Container = createjs.Container;
import Stage = createjs.Stage;
import MouseEvent = createjs.MouseEvent;
import Point = createjs.Point;
import Rectangle = createjs.Rectangle;

export class AbstractCamera {
    protected MIN_MAP_ZOOM: number = 0.7;
    protected MAX_MAP_ZOOM: number = 1.5;
    protected DEFAULT_START_ZOOM: number = 1;
    protected GFX_ZOOM_FACTOR: number = 1;
    private _dpiScaleFactor: number = 1;

    protected ZOOM_SPEED: number = 1;

    protected _worldLayer: Container;
    protected _worldStage: Stage;

    protected PAN_SLOPE: number = 50;

    protected isZooming: boolean = false;
    protected lastUpdate: number = -1;
    protected _currentZoom: number = 1;
    protected leftBorder: number = Number.MAX_VALUE;
    protected rightBorder: number = -Number.MAX_VALUE;
    protected topBorder: number = Number.MAX_VALUE;
    protected bottomBorder: number = -Number.MAX_VALUE;

    protected unscaledLeftBorder: number = Number.MAX_VALUE;
    protected unscaledRightBorder: number = -Number.MAX_VALUE;
    protected unscaledTopBorder: number = Number.MAX_VALUE;
    protected unscaledBottomBorder: number = -Number.MAX_VALUE;

    protected isPanning: boolean = false;
    protected panPoint: Point;
    protected lastPanPosition: Point = new Point();
    protected panVelocity: Point = new Point();
    protected panDirectionVector: Point = new Point();
    protected lastPanTimestamp: number;
    protected enableSliding: boolean = true;
    protected slideSpeed: number;
    protected slideDeceleration: number = 100;
    protected maxSlideSpeed: number = 1500;
    protected enableOverPan: boolean = true;
    protected _overPanInPixels: number = 40;
    protected overPanSpeed: number = 150;
    private deltaTargetPoint: Point = new Point();
    private targetPoint: CameraTargetPoint;

    private _targetZoom: number;
    private _enableDoubleTap: boolean = true;

    protected currentPixelViewPort: Rectangle = new Rectangle();

    public cameraMoveSignal: Signal = new Signal();
    public cameraInteractionSignal: Signal = new Signal();
    public notifyEndPanning: Signal = new Signal();
    public notifyZoomEndSignal: Signal = new Signal();
    public startPanningSignal: Signal = new Signal();

    private _enableUserInteractions: boolean = false;

    protected viewPortBorderLeft: number = 0;
    protected viewPortBorderRight: number = 0;
    protected viewPortBorderTop: number = 0;
    protected viewPortBorderBottom: number = 0;
    protected keepRectInSightSpeed: number = 500;
    protected keepRectInSighBorder: number = 30;

    constructor(worldLayer: Container) {
        this._worldLayer = worldLayer;
        this._worldStage = worldLayer.stage;
        this.activateControls();
        this.refreshBounds();
    }

    public activateControls(): void {
        if (this._enableUserInteractions) {
            return;
        }
        this._enableUserInteractions = true;

        this.worldStage.addEventListener("mousedown", this.bindFunction(this.onMouseDown));    // 'useWeakReference' was true here (addEventListener's last argument in AS3)
        this.worldStage.addEventListener("pressup", this.bindFunction(this.onMouseUp));        // 'useWeakReference' was true here (addEventListener's last argument in AS3)
        this.worldStage.addEventListener("mousewheel", this.bindFunction(this.onMouseWheel));  // 'useWeakReference' was true here (addEventListener's last argument in AS3)
    }

    public deactivateControls(): void {
        this._enableUserInteractions = false;
        this.worldStage.removeEventListener("mousedown", this.bindFunction(this.onMouseDown));
        this.worldStage.removeEventListener("pressup", this.bindFunction(this.onMouseUp));
        this.worldStage.removeEventListener("mousemove", this.bindFunction(this.onMouseMove));
        this.worldStage.removeEventListener("mousewheel", this.bindFunction(this.onMouseWheel));
    }

    protected onMouseMove(event: MouseEvent): void {
        this.panVelocity.x = (this.worldStage.mouseX - this.lastPanPosition.x);
        this.panVelocity.y = (this.worldStage.mouseY - this.lastPanPosition.y);
        this.setLastPanPosition(this.worldStage.mouseX, this.worldStage.mouseY);
        let targetX: number = this.worldStage.mouseX - this.panPoint.x;
        let targetY: number = this.worldStage.mouseY - this.panPoint.y;
        this.setWorldLayerPos(targetX, targetY);
    }

    protected onMouseUp(event: MouseEvent): void {
        this.worldStage.removeEventListener(MouseEvent.MOUSE_MOVE, this.bindFunction(this.onMouseMove));
        let deltaTime: number = (Date.now() - this.lastPanTimestamp) / 1000;
        this.slideSpeed = this.panVelocity.length / deltaTime;
        this.panDirectionVector = this.panVelocity.clone();
        this.panDirectionVector.normalize(1);
        this.panPoint = null;
        if (this.slideSpeed > this.maxSlideSpeed) {
            this.slideSpeed = this.maxSlideSpeed;
        }
        this.isPanning = false;
        this.onEndPanningFunction();
    }

    protected onMouseDown(event: MouseEvent): void {
        if (this.isZooming) {
            return;
        }

        this.stopSliding();
        this.stopAutoZoom();
        this.isPanning = true;
        this.panPoint = new Point((<any>this._worldLayer).mouseX, (<any>this._worldLayer).mouseY);
        this.setLastPanPosition((<any>this.worldStage).mouseX, (<any>this.worldStage).mouseY);
        this.worldStage.addEventListener(MouseEvent.MOUSE_MOVE, this.bindFunction(this.onMouseMove)); // 'useWeakReference' was true here (addEventListener's last argument in AS3)
    }

    protected onMouseWheel(event: MouseEvent): void {
        this.cameraInteractionSignal.dispatch();
        if (this.targetPoint) {
            return;
        }
        let zoomValue: number = event.delta * 3 / 100;
        this.changeZoomByDelta(zoomValue);
        this._targetZoom = this._currentZoom;
        this.zoomEventFinish();

    }

    protected zoomEventFinish(): void {
    }

    protected onWhilePanning(): void {
    }

    public onTapGesture(): void {
        if (!this._enableUserInteractions) {
            return;
        }
        this.onEndPanningFunction();
        this.stopSliding();
    }

    public onDoubleTapGesture(): void {
        if (!this._enableUserInteractions) {
            return;
        }
        if (!this.enableOverPan || this.isPanning) {
            return;
        }
        if (!this._enableDoubleTap) {
            return;
        }
        if (this.isZooming) {
            return;
        }
        this.autoZoomToTapPos();
    }

    public autoZoomToTapPos(): void {
        if (this._currentZoom > this.DEFAULT_START_ZOOM) {
            this._targetZoom = this.MIN_MAP_ZOOM;
        }
        else {
            this._targetZoom = this.MAX_MAP_ZOOM;
        }

        let zoomTime: number = Math.abs(this._targetZoom - this._currentZoom) / this.ZOOM_SPEED;
        let targetPos: Point = new Point((<any>this._worldLayer).mouseX, (<any>this._worldLayer).mouseY);
        let currentCenterPos: Point = this.getCurrentCenterPos();
        let distance: number = targetPos.subtract(currentCenterPos).length;
        let speed: number = distance / zoomTime;
        speed /= this.GFX_ZOOM_FACTOR;
        let cameraCenterPoint: Point = new Point(this.worldStage.stageWidth * 0.5, this.worldStage.stageHeight * 0.5);
        let cameraTargetPoint: CameraTargetPoint = new CameraTargetPoint(targetPos, cameraCenterPoint, speed, speed, speed);
        this.moveToTargetPoint(cameraTargetPoint);
    }

    public initCustomBorder(leftBorder: number, rightBorder: number, topBorder: number, bottomBorder: number): void {
        this.unscaledLeftBorder = leftBorder;
        this.unscaledRightBorder = rightBorder;
        this.unscaledTopBorder = topBorder;
        this.unscaledBottomBorder = bottomBorder;

        let width: number = (this.unscaledRightBorder - this.unscaledLeftBorder) - 3 * this._overPanInPixels;
        let height: number = (this.unscaledBottomBorder - this.unscaledTopBorder) - 3 * this._overPanInPixels;
        let minZoomX: number = this._worldLayer.stage.stageWidth / width;
        let minZoomY: number = this._worldLayer.stage.stageHeight / height;
        this.MIN_MAP_ZOOM = Math.max(this.MIN_MAP_ZOOM, minZoomX);
        this.MIN_MAP_ZOOM = Math.max(this.MIN_MAP_ZOOM, minZoomY);
        this.DEFAULT_START_ZOOM = this.MIN_MAP_ZOOM + ((this.MAX_MAP_ZOOM - this.MIN_MAP_ZOOM) / 2);
        this.setZoomTo(this.DEFAULT_START_ZOOM);
        this.refreshBounds();
    }

    public update(now: number): void {
        if (this.lastUpdate < 0) {
            this.lastUpdate = now;
        }
        this.updateCamera(now);
        this.lastUpdate = now;
    }

    public refreshBounds(): void {
        this.leftBorder = this.unscaledLeftBorder * this._currentZoom;
        this.rightBorder = this._worldLayer.stage.stageWidth - this.unscaledRightBorder * this._currentZoom;
        this.topBorder = this.unscaledTopBorder * this._currentZoom;
        this.bottomBorder = this._worldLayer.stage.stageHeight - this.unscaledBottomBorder * this._currentZoom;
    }

    protected updateCamera(now: number): void {
        this.handleZooming(now);
        this.handleSliding(now);
        this.handleOverPanning(now);
        this.handleTargetPoint(now);
    }

    private handleTargetPoint(now: number): void {
        if (this.targetPoint) {
            let deltaTime: number = (now - this.lastUpdate) / 1000;
            let correctedTargetPoint: Point = this.targetPoint.getCorrectedTargetPoint(this._currentZoom);

            this.deltaTargetPoint.x = this._worldLayer.x - correctedTargetPoint.x;
            this.deltaTargetPoint.y = this._worldLayer.y - correctedTargetPoint.y;
            if (this.deltaTargetPoint.length < deltaTime * this.targetPoint.targetFollowSpeed) {
                this.setWorldLayerPos(correctedTargetPoint.x, correctedTargetPoint.y);
                this.onEndPanningFunction();
                this.resetTargetPoint();
            }
            else {
                this.deltaTargetPoint.normalize(1);
                this.setWorldLayerPos(this._worldLayer.x - this.deltaTargetPoint.x * this.targetPoint.targetFollowSpeed * deltaTime, this._worldLayer.y - this.deltaTargetPoint.y * this.targetPoint.targetFollowSpeed * deltaTime);
                this.targetPoint.targetFollowSpeed += this.targetPoint.targetFollowAcceleration * deltaTime;
                if (this.targetPoint.targetFollowSpeed > this.targetPoint.targetFollowMaxSpeed) {
                    this.targetPoint.targetFollowSpeed = this.targetPoint.targetFollowMaxSpeed;
                }
            }
        }
    }

    public moveToTargetPoint(targetPoint: CameraTargetPoint): void {
        this.targetPoint = targetPoint;
    }

    protected resetTargetPoint(): void {
        this.targetPoint = null;
    }

    private handleSliding(now: number): void {
        if (!this.enableSliding) {
            return;
        }
        let deltaTime: number = (now - this.lastUpdate) / 1000;
        if (this.slideSpeed > 0) {
            this.setWorldLayerPos(this._worldLayer.x + this.panDirectionVector.x * this.slideSpeed * deltaTime, this._worldLayer.y + this.panDirectionVector.y * this.slideSpeed * deltaTime);
            this.slideSpeed -= this.slideDeceleration;
            if (this.slideSpeed <= 0) {
                this.stopSliding();
                this.onEndPanningFunction();
            }
        }
    }

    private handleZooming(now: number): void {
        if (isNaN(this._targetZoom)) {
            this._targetZoom = this._currentZoom;
        }
        if (this._targetZoom == this._currentZoom) {
            return;
        }
        if (!this.enableOverPan || this.isPanning) {
            return;
        }
        if (!this._enableDoubleTap) {
            return;
        }

        let deltaTime: number = (now - this.lastUpdate) / 1000;
        let deltaZoom: number = (this.ZOOM_SPEED * deltaTime);

        if (this._targetZoom > this._currentZoom) {
            this.changeZoomByDelta(deltaZoom);
        }
        else if (this._targetZoom < this._currentZoom) {
            this.changeZoomByDelta(-deltaZoom);
        }
    }

    private stopAutoZoom(): void {
        this._targetZoom = this._currentZoom;
    }

    private handleOverPanning(now: number): void {
        if (!this.enableOverPan || this.isPanning) {
            return;
        }
        let deltaTime: number = (now - this.lastUpdate) / 1000;
        let deltaPixel: number = deltaTime * this.overPanSpeed * this._currentZoom;
        if (this._worldLayer.x > (this.leftBorder - this._overPanInPixels)) {
            this._worldLayer.x -= deltaPixel;
        }
        else if (this._worldLayer.x < (this.rightBorder + this._overPanInPixels)) {
            this._worldLayer.x += deltaPixel;
        }
        if (this._worldLayer.y > (this.topBorder - this._overPanInPixels)) {
            this._worldLayer.y -= deltaPixel;
        }
        else if (this._worldLayer.y < (this.bottomBorder + this._overPanInPixels)) {
            this._worldLayer.y += deltaPixel;
        }
    }

    protected onEndPanningFunction(): void {
        this.notifyEndPanning.dispatch();
    }

    protected onStartPanning(): void {
        this.startPanningSignal.dispatch();
    }

    public centerCamera(): void {
        let posX: number = (this.unscaledRightBorder - this.unscaledLeftBorder) / 2;
        let posY: number = (this.unscaledBottomBorder - this.unscaledTopBorder) / 2;
        this.centerUnscaledPosition(posX, posY);
    }

    public centerRectangle(rect: Rectangle): void {
        let posX: number = rect.left + rect.width / 2;
        let posY: number = rect.top + rect.height / 2;
        let centerPoint: Point = new Point(this.worldStage.stageWidth * 0.5, this.worldStage.stageHeight * 0.5);
        let cameraTargetPoint: CameraTargetPoint = new CameraTargetPoint(new Point(posX, posY), centerPoint, 600 * this.GFX_ZOOM_FACTOR, 1000 * this.GFX_ZOOM_FACTOR, 1000 * this.GFX_ZOOM_FACTOR);
        this.moveToTargetPoint(cameraTargetPoint);
    }

    public keepObjectInSight(rect: Rectangle): void {
        let deltaX: number = 0;
        let deltaY: number = 0;

        if (rect.right > (this.currentPixelViewPort.right - this.viewPortBorderRight) / this._currentZoom) {
            deltaX = (rect.right - (this.currentPixelViewPort.right - this.viewPortBorderRight) / this._currentZoom) + this.keepRectInSighBorder;
        }
        else if (rect.left < (this.currentPixelViewPort.left - this.viewPortBorderLeft) / this._currentZoom) {
            deltaX = (rect.left - (this.currentPixelViewPort.left - this.viewPortBorderLeft) / this._currentZoom) - this.keepRectInSighBorder;
        }

        if (rect.top < (this.currentPixelViewPort.top + this.viewPortBorderTop) / this._currentZoom) {
            deltaY = (rect.top - (this.currentPixelViewPort.top + this.viewPortBorderTop) / this._currentZoom) - this.keepRectInSighBorder;
        }
        else if (rect.bottom > (this.currentPixelViewPort.bottom - this.viewPortBorderBottom) / this._currentZoom) {
            deltaY = (rect.bottom - (this.currentPixelViewPort.bottom - this.viewPortBorderBottom) / this._currentZoom) + this.keepRectInSighBorder;
        }

        if (deltaX != 0 || deltaY != 0) {
            let targetPoint: Point = new Point(this._worldLayer.x - deltaX, this._worldLayer.y - deltaY);
            let centerPoint: Point = new Point(this.worldStage.stageWidth * 0.5, this.worldStage.stageHeight * 0.5);
            let cameraTargetPoint: CameraTargetPoint = new CameraTargetPoint(targetPoint, centerPoint, 600 * this.GFX_ZOOM_FACTOR, 1000 * this.GFX_ZOOM_FACTOR, 1000 * this.GFX_ZOOM_FACTOR, false);
            this.moveToTargetPoint(cameraTargetPoint);
        }
        else {
            this.resetTargetPoint();
        }
    }

    public centerUnscaledPosition(posX: number, posY: number): void {
        this.setWorldLayerPos(this.worldStage.stageWidth * 0.5 - (posX * this._currentZoom), this.worldStage.stageHeight * 0.5 - (posY * this._currentZoom));
        this.resetTargetPoint();
    }

    public centerUnscaledRectangle(rect: Rectangle): void {
        let posX: number = rect.left + rect.width / 2;
        let posY: number = rect.top + rect.height / 2;
        this.setWorldLayerPos(this.worldStage.stageWidth * 0.5 - (posX * this._currentZoom), this.worldStage.stageHeight * 0.5 - (posY * this._currentZoom));
        this.resetTargetPoint();
    }

    public setWorldLayerPos(posX: number, posY: number): void {
        this._worldLayer.x = posX;
        this._worldLayer.y = posY;
        this.currentPixelViewPort.x = -posX;
        this.currentPixelViewPort.y = -posY;
        this.currentPixelViewPort.width = this.worldStage.stageWidth;
        this.currentPixelViewPort.height = this.worldStage.stageHeight;
        this.pushToBounds();
        this.updateViewport();
    }

    protected setWorldLayerScale(scale: number): void {
        this._worldLayer.scaleX = scale;
        this._worldLayer.scaleY = scale;
        this._currentZoom = scale;
        this.refreshBounds();
    }

    protected pushToBounds(): void {
        if (this._worldLayer.x > this.leftBorder) {
            this._worldLayer.x = this.leftBorder;
            this.panDirectionVector.x = 0;
        }
        if (this._worldLayer.x < this.rightBorder) {
            this._worldLayer.x = this.rightBorder;
            this.panDirectionVector.x = 0;
        }
        if (this._worldLayer.y > this.topBorder) {
            this._worldLayer.y = this.topBorder;
            this.panDirectionVector.y = 0;
        }
        if (this._worldLayer.y < this.bottomBorder) {
            this._worldLayer.y = this.bottomBorder;
            this.panDirectionVector.y = 0;
        }
    }

    protected stopSliding(): void {
        this.slideSpeed = 0;
    }

    protected setLastPanPosition(x: number, y: number): void {
        this.lastPanPosition.x = x;
        this.lastPanPosition.y = y;
        this.lastPanTimestamp = Date.now();
    }

    public changeZoomByDelta(deltaZoom: number): void {
        if (!this._enableUserInteractions) {
            return;
        }
        if (deltaZoom == 0) {
            return;
        }
        let newZoom: number = this._currentZoom + deltaZoom;
        if (newZoom < this.MIN_MAP_ZOOM) {
            newZoom = this.MIN_MAP_ZOOM;
        }
        else if (newZoom > this.MAX_MAP_ZOOM) {
            newZoom = this.MAX_MAP_ZOOM;
        }
        if (this._currentZoom == newZoom) {
            return;
        }
        let unscaledX: number = -(this._worldLayer.x / this._currentZoom - (this.worldStage.stageWidth / 2) / this._currentZoom);
        let unscaledY: number = -(this._worldLayer.y / this._currentZoom - (this.worldStage.stageHeight / 2) / this._currentZoom);
        this.setWorldLayerScale(newZoom);
        this.setWorldLayerPos(this.worldStage.stageWidth * 0.5 - (unscaledX * newZoom), this.worldStage.stageHeight * 0.5 - (unscaledY * newZoom));
    }

    public setZoomTo(newZoom: number): void {
        if (isNaN(newZoom)) {
            newZoom = this.DEFAULT_START_ZOOM;
        }
        this.changeZoomByDelta(newZoom - this._currentZoom);
    }

    protected get worldStage(): Stage {
        return this._worldStage;
    }

    public updateViewport(): void {
        this.cameraMoveSignal.dispatch();
    }

    public getCurrentCenterPos(): Point {
        let unscaledX: number = -(this._worldLayer.x / this._currentZoom - (this.worldStage.stageWidth / 2) / this._currentZoom);
        let unscaledY: number = -(this._worldLayer.y / this._currentZoom - (this.worldStage.stageHeight / 2) / this._currentZoom);
        return new Point(unscaledX, unscaledY);
    }

    public destroy(): void {
        this.deactivateControls();
        this._worldLayer = null;
        this.cameraMoveSignal.removeAll();
        this.cameraInteractionSignal.removeAll();
        this.notifyEndPanning.removeAll();
        this.notifyZoomEndSignal.removeAll();
    }

    public get currentZoom(): number {
        return this._currentZoom;
    }

    public set overPanInPixels(value: number) {
        this._overPanInPixels = value;
    }

    public get enableDoubleTap(): boolean {
        return this._enableDoubleTap;
    }

    public set enableDoubleTap(value: boolean) {
        this._enableDoubleTap = value;
    }

    public set dpiScaleFactor(value: number) {
        this._dpiScaleFactor = value;
    }
}
